import * as React from 'react';
import { createRefetchContainer, graphql } from 'react-relay';
import Grid from '@material-ui/core/Grid';
import { Decimal } from 'decimal.js';
import Drawer from '@material-ui/core/Drawer';
import MoreHorizIcon from '@material-ui/icons/MoreHoriz';
import FilterIcon from '@material-ui/icons/FilterList';
import IconButton from '@material-ui/core/IconButton';
import {
  CallMerge, DynamicFeed, GetApp, Publish, 
} from '@material-ui/icons';
import { Tooltip } from '@material-ui/core';
import PaginatedTable from '../common/PaginatedTable';
import { useCommonStyles } from '../common/Styles';
import TableFieldMeta from '../../common/TableFieldMeta';
import TransferApprovalCell from '../common/TransferApprovalCell';
import { createQueryRendererProgress } from '../common/QueryRendererProgress';
import AddDepositFormBase from './AddDepositFormBase';
import AddWithdrawalFormBase from './AddWithdrawalFormBase';
import reconcileCashTransferRequests from '../../mutations/ReconcileCashTransferRequests';
import BulkAddWithdrawalForm from './BulkAddWithdrawalForm';
import { doCommitPromise } from '../../common/commit';
import createCashTransferRequestBatch from '../../mutations/CreateCashTransferRequestBatch';
import BatchCell from './cashTransferRequestList/BatchCell';
import BatchDetailsFormBase from './cashTransferRequestList/BatchDetailsFormBase';
import TableIconButton from '../common/table/TableIconButton';
import CashTransferRequestDetailsFormBase from './CashTransferRequestDetailsFormBase';
import SelectionCell from './cashTransferRequestList/SelectionCell';
import InnerTabFilterToggleButtonGroup from '../common/innerTab/InnerTabFilterToggleButtonGroup';
import InnerTabFilterGridItemTextField from '../common/innerTab/InnerTabFilterGridItemTextField';
import BaseGridContainer from '../common/BaseGridContainer';
import FilterListOff from '../../icons/FilterListOff';
import Block from '../../icons/Block';
import FileUpload from '../../icons/FileUpload';
import InnerTabGridItemButtonGroup from '../common/innerTab/InnerTabGridItemIconButtonGroup';
import InnerTabIconButton from '../common/innerTab/InnerTabIconButton';

const statusDef = [
  ['VER', 'Verified', true],
  ['CAN', 'Cancelled', false],
  ['APR', 'Approved', false],
  ['REC', 'Reconciled', false],
];

const initialFilterStatus = {
  showStatus: statusDef.map(x => x[2]),
  filterApproved: true,
  approved: 0,
  search: '',
  contraParty: null,
};

const isApprovedOnceApplicable = (showStatus) => {
  let applicable = true;
  showStatus.forEach((item, index) => {
    if (item) {
      if (statusDef[index][0] === 'VER') {
        applicable = applicable && item;
      } else {
        applicable = applicable && !item;
      }
    }
  });
  return applicable;
};

const filterStatusToVars = (ucVar) => {
  // const defaultStatusFilter = ['PEN', 'VER']; //, 'CAN', 'APR', 'VER'
  const statusIn = [];
  ucVar.showStatus.forEach((item, index) => {
    if (item) {
      statusIn.push(statusDef[index][0]);
    }
  });
  let approved = null;
  if (isApprovedOnceApplicable(ucVar.showStatus)) {
    approved = ucVar.filterApproved ? ucVar.approved : null;
  }

  return {
    search: ucVar.search || null,
    contraParty: ucVar.contraParty || null,
    approved,
    statusIn,
  };
};

const boolArrayToItems = (boolArray) => {
  const items = [];
  boolArray.forEach((item, index) => {
    if (item) {
      // items.push(index);
      items.push(statusDef[index][0]);
    }
  });
  return items;
};

function CashTransferRequestList(props) {
  const [
    cashTransactionSelectedRow,
    setCashTransactionSelectedRow,
  ] = React.useState(null);
  const [selectionSet, setSelectionSet] = React.useState([]);
  const [showStatus, setShowStatus] = React.useState(
    initialFilterStatus.showStatus,
  );
  const [filterApproved, setFilterApproved] = React.useState(
    initialFilterStatus.filterApproved,
  );
  const [approved, setApproved] = React.useState(initialFilterStatus.approved);
  const [showDepositDrawer, setShowDepositDrawer] = React.useState(false);
  const [showWithdrawDrawer, setShowWithdrawDrawer] = React.useState(false);
  const [showBulkWithdrawDrawer, setShowBulkWithdrawDrawer] = React.useState(
    false,
  );
  const [showDetails, setShowDetails] = React.useState(false);
  // const [positionTransferInfo, setPositionTransferInfo] = React.useState(false);
  const [batch, setBatch] = React.useState(false);
  const [tableRef] = React.useState(React.createRef());
  const [ucVar] = React.useState({
    refreshRequestSeq: 0,
    cashTransferRequestId: null,
    ...initialFilterStatus,
  });

  const {
    accountId,
    msgRouter,
    viewer,
    relay,
    custodian,
    contraCustodian,
    contraOnly,
    contraPartyFilter,
  } = props;

  const classes = useCommonStyles();
  const accountColumns = accountId
    ? []
    : [
      TableFieldMeta.account(node => node.account),
      {
        accessor: (node) => {
          if (node.account.clientaccount) {
            return node.account.clientaccount.referenceNumber;
          }
          return '';
        },
        id: 'referenceNumber',
        Header: 'Reference Number',
      },
    ];

  const renderTransferApprovalCell = (row) => {
    const transferRequest = row.value;
    return (
      <TransferApprovalCell
        key={transferRequest.updatedAt}
        transferRequest={transferRequest}
        ibCashTransfer={cashTransactionSelectedRow}
      />
    );
  };

  const columns = [
    TableFieldMeta.localId,
    ...accountColumns,
    TableFieldMeta.priceField('amount', 'Amount', 'amount', 'currency'),
    {
      accessor: (node) => {
        if (node.bankAccount) {
          const { bankAccount } = node;
          return `${bankAccount.bsbNumber}-${bankAccount.accountNumber} ${bankAccount.holderName}`;
        }
        return '';
      },
      Header: 'Bank Account',
      id: 'bankAccount',
      ...TableFieldMeta.leftAlignStyle,
    },
    {
      accessor: node => node,
      Header: '',
      id: 'link',
      ...TableFieldMeta.centerAlignStyle,
      Cell: row => renderTransferApprovalCell(row),
    },
    TableFieldMeta.fieldCell(
      'selection',
      '',
      row => (
        <SelectionCell
          cashTransferReqeust={row.value}
          selectionSet={selectionSet}
          setSelectionSet={setSelectionSet}
        />
      ),
      { accessor: x => x },
    ),
    TableFieldMeta.fieldCell(
      'batch',
      'Batch',
      row => (
        <BatchCell
          key={row.value.id}
          cashTransferRequest={row.value}
          onInvalidate={() => tableRef.current.refreshData()}
          onBatchSelect={_batch => setBatch(_batch)}
        />
      ),
      { align: 'center', accessor: x => x },
    ),
    TableFieldMeta.fieldCell(
      'details',
      'Details',
      row => (
        <TableIconButton
          key={`${row.value.id}-inverse-batch`}
          iconClass={MoreHorizIcon}
          color="primary"
          onClick={() => {
            ucVar.cashTransferRequestId = row.value.id;
            setShowDetails(true);
          }}
        />
      ),
      { align: 'center', accessor: x => x },
    ),
  ];

  const onPublishState = (client, state) => {
    const { cashTransactionSelectedRow: selection, refreshRequestSeq } = state;
    setCashTransactionSelectedRow(selection);

    if (refreshRequestSeq !== ucVar.refreshRequestSeq) {
      tableRef.current.refreshData();
      ucVar.refreshRequestSeq = refreshRequestSeq;
    }
  };

  msgRouter.addStateListener(onPublishState);

  const transferSum = selectionSet.reduce(
    (accumulator, currentValue) => accumulator.plus(new Decimal(currentValue.amount)),
    new Decimal(0),
  );

  let transfersMatching = cashTransactionSelectedRow
    && new Decimal(cashTransactionSelectedRow.amount).equals(transferSum);
  transfersMatching = selectionSet.reduce(
    (v, transfer) => v
      && (transfer.amount < 0
        ? transfer.status === 'VER'
        : transfer.status === 'VER' || transfer.status === 'APR'),
    transfersMatching,
  );

  const allBatchUnset = selectionSet.reduce(
    (v, transfer) => v && transfer.batch !== null,
    true,
  );

  React.useEffect(
    () => function cleanup() {
      msgRouter.removeStateListener(onPublishState);
    },
  );

  if (viewer === null) return <React.Fragment />;
  return (
    <div className={classes.innerTabRoot}>
      <Drawer
        anchor="right"
        open={showDepositDrawer}
        onClose={() => setShowDepositDrawer(false)}
      >
        <div className={classes.drawerRoot}>
          <AddDepositFormBase
            onAdd={() => tableRef.current.refreshData()}
            onClose={() => setShowDepositDrawer(false)}
            custodian={custodian}
            accountId={accountId}
          />
        </div>
      </Drawer>

      <Drawer
        anchor="right"
        open={showDetails}
        onClose={() => setShowDetails(false)}
      >
        <div className={classes.drawerRoot}>
          <CashTransferRequestDetailsFormBase
            getCashTransferRequestId={() => ucVar.cashTransferRequestId}
          />
        </div>
      </Drawer>

      <Drawer
        anchor="right"
        open={showWithdrawDrawer}
        onClose={() => setShowWithdrawDrawer(false)}
      >
        <div className={classes.drawerRoot}>
          <AddWithdrawalFormBase
            onAdd={() => tableRef.current.refreshData()}
            onClose={() => setShowWithdrawDrawer(false)}
            custodian={custodian}
            accountId={accountId}
          />
        </div>
      </Drawer>

      <Drawer
        anchor="right"
        open={showBulkWithdrawDrawer}
        onClose={() => setShowBulkWithdrawDrawer(false)}
      >
        <BulkAddWithdrawalForm
          onAdd={() => tableRef.current.refreshData()}
          onClose={() => setShowBulkWithdrawDrawer(false)}
          custodian={custodian}
        />
      </Drawer>

      <Drawer anchor="right" open={batch} onClose={() => setBatch(null)}>
        <div className={classes.drawerRoot}>
          <BatchDetailsFormBase
            onAdd={() => {
              tableRef.current.refreshData();
              setBatch(null);
            }}
            onClose={() => setBatch(null)}
            batchId={batch && batch.id}
            contraCustodian={contraCustodian}
            contraOnly={contraOnly}
          />
        </div>
      </Drawer>

      <BaseGridContainer>
        <InnerTabFilterGridItemTextField
          id="search-filter"
          label="Custom Search"
          helperText="Account name, Amount, ID"
          onChange={e => null}
        />

        <InnerTabFilterToggleButtonGroup
          enumType="CashTransferStatus"
          defaultValue={[...boolArrayToItems(showStatus)]}
          idToolTipMode
          suppressIds={['PEN']}
          onChange={(newVals) => {
            for (let i = 0; i < statusDef.length; i += 1) {
              ucVar.showStatus[i] = newVals.includes(statusDef[i][0]);
            }
            setShowStatus([...ucVar.showStatus]);
            tableRef.current.refreshData();
          }}
          helperText="Status Filter"
          exclusive={false}
        />

        <InnerTabFilterGridItemTextField
          id="approval-count"
          label=" "
          helperText="Approval Count"
          // onChange={e => null}
          onChange={(e) => {
            ucVar.approved = Number(e.target.value);
            setApproved(ucVar.approved);
            tableRef.current.refreshData();
          }}
          defaultValue={approved}
          type="number"
          halfWidth
          InputProps={{
            endAdornment: (
              <IconButton
                aria-label="toggle password visibility"
                // onMouseDown={handleMouseDownPassword}
                edge="end"
                style={{ paddingTop: 0, paddingBottom: 0 }}
                color={filterApproved ? 'primary' : 'default'}
                onClick={() => {
                  ucVar.filterApproved = !filterApproved;
                  setFilterApproved(ucVar.filterApproved);
                  tableRef.current.refreshData();
                }}
              >
                {filterApproved ? <FilterIcon /> : <FilterListOff />}
              </IconButton>
            ),
          }}
        />

        {contraPartyFilter && (
          <InnerTabFilterToggleButtonGroup
            withNull
            enumType="ContraParty"
            helperText="Contra Party"
            defaultValue={null}
            idToolTipMode
            suppressIds={['BK']}
            onChange={(newValue) => {
              ucVar.contraParty = newValue || null;
              tableRef.current.refreshData();
            }}
          />
        )}

        <InnerTabGridItemButtonGroup>
          <Tooltip title="Create Deposit">
            <IconButton
              aria-label="creat-withdrawal"
              color="primary"
              onClick={() => setShowDepositDrawer(true)}
            >
              <Publish />
            </IconButton>
          </Tooltip>

          <Tooltip title="Create Withdrawal">
            <IconButton
              aria-label="create-deposit"
              color="primary"
              onClick={() => setShowWithdrawDrawer(true)}
            >
              <GetApp />
            </IconButton>
          </Tooltip>

          <Tooltip title="Withdrawal File Upload">
            <IconButton
              aria-label="create-transfer-from-file"
              color="primary"
              onClick={() => setShowBulkWithdrawDrawer(true)}
            >
              <FileUpload />
            </IconButton>
          </Tooltip>
        </InnerTabGridItemButtonGroup>

        <InnerTabFilterGridItemTextField
          id="search-filter"
          label="Selection"
          helperText={`Entries: ${selectionSet.length}`}
          onChange={() => null}
          value={transferSum.toString()}
          type="number"
          disabled
        />

        <InnerTabGridItemButtonGroup>
          <InnerTabIconButton
            color="primary"
            disabled={allBatchUnset}
            iconClass={DynamicFeed}
            tooltip="Reconcile"
            onClickPromise={() => doCommitPromise(
              createCashTransferRequestBatch,
              {
                cashTransferRequestIds: selectionSet.map(x => x.id),
                name: 'Batch ',
              },
              () => setSelectionSet([]),
              () => null,
            )}
          />

          <Tooltip title="Reconcile">
            <IconButton
              disabled={!transfersMatching}
              aria-label="reconcile"
              color="primary"
              onClick={() => doCommitPromise(
                reconcileCashTransferRequests,
                {
                  transferRequestIds: selectionSet.map(
                    element => element.id,
                  ),
                  brokerTransferId: cashTransactionSelectedRow.id,
                },
                () => setSelectionSet([]),
                () => null,
              )
              }
            >
              <CallMerge />
            </IconButton>
          </Tooltip>

          <Tooltip title="Clear Selection">
            <IconButton
              aria-label="clear-selection"
              color="primary"
              disabled={selectionSet.length === 0}
              onClick={() => setSelectionSet([])}
            >
              <Block />
            </IconButton>
          </Tooltip>
        </InnerTabGridItemButtonGroup>
      </BaseGridContainer>

      <Grid container spacing={2}>
        <Grid sm={12} md={12}>
          <PaginatedTable
            // tableRender={RTTableRenderer}
            ref={tableRef}
            columns={columns}
            viewer={viewer}
            getDataNode={() => (viewer ? viewer.cashTransferRequests : null)}
            getFetchParams={() => ({
              accountId,
              ...filterStatusToVars(ucVar),
            })}
            relay={relay}
          />
        </Grid>
      </Grid>
    </div>
  );
}

export const query = graphql`
  query CashTransferRequestListQuery(
    $first: Int
    $offset: Int
    $search: String
    $accountId: ID
    $statusIn: [String]
    $approved: Float
    $accountType: String
    $custodian: String
    $contraParty: String
  ) {
    viewer {
      ...CashTransferRequestList_viewer
        @arguments(
          first: $first
          offset: $offset
          search: $search
          accountId: $accountId
          statusIn: $statusIn
          approved: $approved
          accountType: $accountType
          custodian: $custodian
          contraParty: $contraParty
        )
    }
  }
`;

const reFetched = createRefetchContainer(
  CashTransferRequestList,
  {
    viewer: graphql`
      fragment CashTransferRequestList_viewer on Query
        @argumentDefinitions(
          first: { type: Int, defaultValue: 10 }
          offset: { type: Int, defaultValue: 0 }
          search: { type: String }
          accountId: { type: ID, defaultValue: null }
          statusIn: { type: "[String]", defaultValue: [] }
          approved: { type: Float }
          accountType: { type: String }
          custodian: { type: String }
          contraParty: { type: String }
        ) {
        cashTransferRequests(
          first: $first
          offset: $offset
          search: $search
          orderBy: "-createdAt"
          account: $accountId
          statusIn: $statusIn
          approved: $approved
          accountType: $accountType
          custodian: $custodian
          contraParty: $contraParty
        ) {
          edges {
            node {
              id
              batch {
                id
                name
              }

              currency
              amount
              createdAt
              updatedAt
              status
              blocked
              custodian
              currency
              subtype
              bankAccount {
                accountNumber
                bsbNumber
                holderName
              }
              account {
                id
                name
                clientaccount {
                  referenceNumber
                }
              }

              cashtransferrequestapprovalSet {
                edges {
                  node {
                    approvedAt
                    approvedBy {
                      fullName
                    }
                  }
                }
              }
            }
          }
          count
        }
      }
    `,
  },
  query,
);

export default createQueryRendererProgress(
  query,
  reFetched,
  (
    viewer,
    {
      msgRouter,
      accountId,
      accountType,
      custodian,
      contraCustodian,
      contraOnly,
      contraPartyFilter,
    },
  ) => ({
    msgRouter,
    accountId,
    accountType,
    custodian,
    contraCustodian,
    contraOnly,
    contraPartyFilter,
  }),
  ({
    accountId, accountType, custodian, contraCustodian, 
  }) => ({
    accountId,
    accountType,
    custodian,
    contraCustodian,
    ...filterStatusToVars(initialFilterStatus),
  }),
);
